import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateStepStatus, getAppEmbedUrl, getWidgetStatus } from '../api/onboarding';
import { getInitialSyncStatus, syncAll } from '../api/knowledgeBase';
import '../styles/pages/onboarding.css';


// Confetti library (loaded dynamically on completion):
// https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="step-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

/**
 * Skeleton placeholder for the onboarding page while data loads.
 */
const OnboardingSkeleton = () => (
  <div className="onboarding-skeleton">
    {/* Progress card skeleton */}
    <div className="skeleton-progress-card">
      <div className="skeleton-row">
        <div className="skeleton-text skeleton-text--sm" />
        <div className="skeleton-text skeleton-text--xs" />
      </div>
      <div className="skeleton-bar" />
      <div className="skeleton-text skeleton-text--md" />
    </div>

    {/* Steps skeleton */}
    <div className="skeleton-steps">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="skeleton-step">
          <div className="skeleton-circle" />
          <div className="skeleton-step-content">
            <div className="skeleton-text skeleton-text--lg" />
            <div className="skeleton-text skeleton-text--md" />
          </div>
          <div className="skeleton-text skeleton-text--btn" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Activation overlay modal shown during widget activation flow.
 */
const ActivationOverlay = ({ phase, t, onOpenThemeEditor }) => {
  // phase: 'loading' | 'success' | 'error' | 'polling'
  return (
    <div className="activation-overlay">
      <div className="activation-overlay__backdrop" />
      <div className="activation-overlay__modal">
        {(phase === 'loading' || phase === 'polling') && (
          <>
            <div className="activation-overlay__spinner" />
            <h3 className="activation-overlay__title">{t('shopify.activating')}</h3>
            <p className="activation-overlay__text">
              {phase === 'polling' ? t('shopify.activatingPolling') : t('shopify.activatingDesc')}
            </p>
          </>
        )}
        {phase === 'error' && (
          <>
            <div className="activation-overlay__warning">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="activation-overlay__title">{t('shopify.activationFailed')}</h3>
            <p className="activation-overlay__text">{t('shopify.activationFailedDesc')}</p>
            <button className="activation-overlay__btn" onClick={onOpenThemeEditor}>
              {t('shopify.openThemeEditor')}
            </button>
          </>
        )}
        {phase === 'success' && (
          <>
            <div className="activation-overlay__check">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="16 8 10 16 7 13" />
              </svg>
            </div>
            <h3 className="activation-overlay__title">{t('shopify.activationSuccess')}</h3>
            <p className="activation-overlay__text">{t('shopify.activationSuccessDesc')}</p>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * OnboardingPage Component
 *
 * Props:
 * - items: Array of onboarding step objects (managed by parent for sidebar pill access)
 * - setItems: State setter for items
 * - loading: Boolean indicating if items are still loading
 *
 * Step object shape:
 * {
 *   id: number,
 *   key: string,              // Semantic identifier (e.g., 'shopify-connected', 'activate-widget')
 *   title: string,
 *   description: string,
 *   status: 'pending' | 'visited' | 'completed' | 'skipped',
 *   type: 'required' | 'optional' | 'recommended',
 *   action: string | null,
 *   capabilityLine?: string,    // Step 1: benefit statement
 *   syncedLine?: string,        // Step 1 & 2: synced data summary
 * }
 */
const STEP_ROUTES = {
  'shopify-connected': '/knowledge-base?tab=products',
  'sync-content': '/knowledge-base?tab=pages',
  'review-settings': '/settings',
  'test-bot': '/test-bot',
  'customize-widget': '/widget',
};

const OnboardingPage = ({ items, setItems, loading = false }) => {
  const { t } = useTranslation('onboarding');
  // Step text comes from the api defaults (English); translate by step id with a fallback.
  const stepText = (item, field) => t(`shopify.steps.${item.id}.${field}`, { defaultValue: item[field] || '' });
  const navigate = useNavigate();
  const [loadingSteps, setLoadingSteps] = useState({}); // { [stepKey]: true }
  const [activationPhase, setActivationPhase] = useState(null); // null | 'loading' | 'error' | 'polling' | 'success'
  const [hasCelebrated, setHasCelebrated] = useState(false);
  const [syncNotification, setSyncNotification] = useState(null); // 'started' | 'error' | null
  const [showComplete, setShowComplete] = useState(false);
  const syncChecked = useRef(false);

  const handleSkip = (idOrKey) => {
    setItems(items.map((item) =>
      (item.id === idOrKey || item.key === idOrKey
        ? { ...item, status: 'skipped' }
        : item)
    ));
    // Fire-and-forget -- don't block UI
    updateStepStatus(idOrKey, 'skipped');
  };

  /**
   * Fire confetti animation using dynamically loaded canvas-confetti library.
   */
  const fireConfetti = useCallback(() => {
    const loadAndFire = () => {
      if (window.confetti) {
        // Three bursts with different angles for a nice effect
        const defaults = { origin: { y: 0.7 }, zIndex: 10001 };
        window.confetti({ ...defaults, angle: 60, spread: 55, particleCount: 80 });
        window.confetti({ ...defaults, angle: 90, spread: 100, particleCount: 100 });
        window.confetti({ ...defaults, angle: 120, spread: 55, particleCount: 80 });
        return;
      }
      // Load confetti library dynamically
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
      script.onload = () => {
        if (window.confetti) {
          const defaults = { origin: { y: 0.7 }, zIndex: 10001 };
          window.confetti({ ...defaults, angle: 60, spread: 55, particleCount: 80 });
          window.confetti({ ...defaults, angle: 90, spread: 100, particleCount: 100 });
          window.confetti({ ...defaults, angle: 120, spread: 55, particleCount: 80 });
        }
      };
      document.head.appendChild(script);
    };
    loadAndFire();
  }, []);

  /**
   * Navigate to the step's page. The step stays in its current status —
   * only explicit "Mark as Done" or "Skip" changes the status.
   */
  const handleVisitStep = async (idOrKey) => {
    const step = items.find(item => item.id === idOrKey || item.key === idOrKey);

    // Navigate to the relevant page
    const route = STEP_ROUTES[step?.key];
    if (route) {
      navigate(route);
    }
  };

  /**
   * Mark a step as completed — the user's conscious "done" action.
   */
  const handleMarkAsDone = async (idOrKey) => {
    const step = items.find(item => item.id === idOrKey || item.key === idOrKey);
    const stepKey = step?.key || idOrKey;

    setLoadingSteps(prev => ({ ...prev, [stepKey]: true }));

    try {
      await updateStepStatus(idOrKey, 'completed');
      setItems(items.map((item) =>
        (item.id === idOrKey || item.key === idOrKey
          ? { ...item, status: 'completed' }
          : item)
      ));
    } catch (err) {
      console.error('Failed to mark step as done:', err);
      // Still update UI
      setItems(items.map((item) =>
        (item.id === idOrKey || item.key === idOrKey
          ? { ...item, status: 'completed' }
          : item)
      ));
    } finally {
      setLoadingSteps(prev => {
        const next = { ...prev };
        delete next[stepKey];
        return next;
      });
    }
  };

  /**
   * Handle the activate-widget step (special flow with overlay + confetti).
   * Awaits the real API response instead of a fake timer.
   */
  const handleActivate = async (idOrKey) => {
    setActivationPhase('loading');

    // Open theme editor as popup so merchant stays on this page
    let popup = null;
    try {
      const data = await getAppEmbedUrl();
      const editorUrl = data?.app_embed_url || data?.redirect_url;
      if (editorUrl) {
        popup = window.open(editorUrl, 'shopify-editor', 'width=1200,height=800,scrollbars=yes');
      }
    } catch {
      // If we can't get the URL, continue to polling — merchant can find it in Shopify admin
    }

    // Poll widget status until enabled (or timeout)
    setActivationPhase('polling');
    const MAX_POLLS = 24; // ~2 minutes at 5s intervals
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      try {
        const result = await getWidgetStatus();
        if (result.status === 'ENABLED') {
          // Auto-close the popup if still open
          if (popup && !popup.closed) popup.close();
          // Widget is now active — mark step complete and celebrate
          updateStepStatus(idOrKey, 'completed').catch(() => {});
          setItems(prev => prev.map((item) =>
            (item.id === idOrKey || item.key === idOrKey
              ? { ...item, status: 'completed' }
              : item)
          ));
          // Brief pause before showing success so activation feels intentional
          await new Promise(resolve => setTimeout(resolve, 2000));
          setActivationPhase('success');
          fireConfetti();
          await new Promise(resolve => setTimeout(resolve, 3000));
          setActivationPhase(null);
          navigate('/shopify');
          return;
        }
      } catch {
        // Not yet — keep polling
      }
    }
    // Polling timed out
    setActivationPhase('error');
  };

  /**
   * Opens the Shopify theme editor in a new tab, then polls until
   * the widget is activated manually.
   */
  const handleOpenThemeEditor = async () => {
    let popup = null;
    try {
      const data = await getAppEmbedUrl();
      const editorUrl = data?.app_embed_url || data?.redirect_url;
      if (editorUrl) {
        popup = window.open(editorUrl, 'shopify-editor', 'width=1200,height=800,scrollbars=yes');
      }
    } catch {
      // If we can't get the URL, the user can still find it in Shopify admin
    }

    // Switch to polling phase — check widget status every 5 seconds
    setActivationPhase('polling');
    const MAX_POLLS = 24; // ~2 minutes at 5s intervals
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      try {
        const result = await getWidgetStatus();
        if (result.status === 'ENABLED') {
          if (popup && !popup.closed) popup.close();
          updateStepStatus('activate-widget', 'completed').catch(() => {});
          setItems(prev => prev.map((item) =>
            (item.key === 'activate-widget'
              ? { ...item, status: 'completed' }
              : item)
          ));
          // Brief pause before showing success so activation feels intentional
          await new Promise(resolve => setTimeout(resolve, 2000));
          setActivationPhase('success');
          fireConfetti();
          await new Promise(resolve => setTimeout(resolve, 3000));
          setActivationPhase(null);
          navigate('/shopify');
          return;
        }
      } catch {
        // Not yet — keep polling
      }
    }
    // Polling timed out — go back to error state
    setActivationPhase('error');
  };

  const completed = items.filter((i) => i.status === 'completed').length;
  const total = items.length;
  const requiredItems = items.filter((i) => i.type === 'required');
  const requiredRemaining = requiredItems.filter((i) => i.status !== 'completed').length;
  const allRequiredDone = requiredRemaining === 0;
  const progressCount = items.filter((i) => i.status === 'completed' || i.status === 'skipped').length;
  const allDone = progressCount === total && total > 0;

  // Delay showing the "Setup Complete" state by 3 seconds
  useEffect(() => {
    if (!allDone) { setShowComplete(false); return; }
    const timer = setTimeout(() => setShowComplete(true), 3000);
    return () => clearTimeout(timer);
  }, [allDone]);

  // Check initial sync status on mount and trigger sync-all if not yet synced
  useEffect(() => {
    if (syncChecked.current) return;
    syncChecked.current = true;

    (async () => {
      try {
        const status = await getInitialSyncStatus();
        if (!status?.initial_sync_done) {
          await syncAll();
          // Show notification after a 3-second delay so it doesn't appear too quickly
          setTimeout(() => {
            setSyncNotification('started');
            // Auto-dismiss after 8 seconds
            setTimeout(() => setSyncNotification(null), 8000);
          }, 3000);
        }
      } catch (err) {
        console.error('Initial sync check failed:', err);
        setSyncNotification('error');
        setTimeout(() => setSyncNotification(null), 8000);
      }
    })();
  }, []);

  // ---- Renderers ----

  const renderStepCircle = (item) => {
    const stepKey = item.key || item.id;
    if (loadingSteps[stepKey]) return <div className="step-circle step-circle--loading"><SpinnerIcon /></div>;
    if (item.status === 'completed') return <div className="step-circle step-circle--completed"><CheckIcon /></div>;
    if (item.status === 'skipped') return <div className="step-circle step-circle--skipped"><DashIcon /></div>;
    return <div className="step-circle step-circle--pending">{item.id}</div>;
  };

  const renderBadge = (item) => {
    if (item.status !== 'pending') return null;
    if (item.type === 'required') return <span className="step-badge step-badge--required">{t('shopify.required')}</span>;
    if (item.type === 'recommended') return <span className="step-badge step-badge--recommended">{t('shopify.recommended')}</span>;
    if (item.type === 'optional') return <span className="step-badge step-badge--optional">{t('shopify.optional')}</span>;
    return null;
  };

  const renderAction = (item) => {
    const stepKey = item.key || item.id;
    const isLoading = loadingSteps[stepKey];

    // Completed: show action button with navigation if route exists
    if (item.status === 'completed') {
      const route = STEP_ROUTES[item.key];
      return item.action ? (
        <button className="step-action__btn step-action__btn--default" onClick={route ? () => navigate(route) : undefined}>
          {stepText(item, "action")}
        </button>
      ) : null;
    }
    // Skipped: show configure button to allow re-entry
    if (item.status === 'skipped') {
      return (
        <button
          className="step-action__btn step-action__btn--outlined"
          onClick={() => handleVisitStep(item.key || item.id)}
          disabled={isLoading}
        >
          {isLoading ? t('shopify.processing') : t('shopify.configure')}
        </button>
      );
    }
    // Activate step: pink CTA, disabled until other required steps done
    const isActivateStep = item.key === 'activate-widget';
    const allPriorDone = isActivateStep && items.filter(i => i.key !== 'activate-widget').every(i => i.status === 'completed' || i.status === 'skipped');
    if (isActivateStep) {
      return (
        <button
          className="step-action__btn step-action__btn--primary"
          onClick={() => handleActivate(item.key || item.id)}
          disabled={!allPriorDone || isLoading}
        >
          {t('shopify.activateNow')}
        </button>
      );
    }
    // Optional/Recommended: skip link + action button + mark as done
    if (item.type === 'optional' || item.type === 'recommended') {
      return (
        <>
          <span className="step-action__skip" onClick={() => handleSkip(item.key || item.id)}>{t('shopify.skip')}</span>
          <button
            className="step-action__btn step-action__btn--outlined"
            onClick={() => handleVisitStep(item.key || item.id)}
            disabled={isLoading}
          >
            {isLoading ? t('shopify.processing') : stepText(item, 'action')}
          </button>
          <button
            className="step-action__btn step-action__btn--mark-done"
            onClick={() => handleMarkAsDone(item.key || item.id)}
            disabled={isLoading}
          >
            {isLoading ? t('shopify.processing') : (<><CheckIcon /> {t('shopify.markAsDone', 'Mark as Done')}</>)}
          </button>
        </>
      );
    }
    // Required: action button + mark as done
    return (
      <>
        <button
          className="step-action__btn step-action__btn--outlined"
          onClick={() => handleVisitStep(item.key || item.id)}
          disabled={isLoading}
        >
          {isLoading ? t('shopify.processing') : stepText(item, 'action')}
        </button>
        <button
          className="step-action__btn step-action__btn--mark-done"
          onClick={() => handleMarkAsDone(item.key || item.id)}
          disabled={isLoading}
        >
          {isLoading ? t('shopify.processing') : (<><CheckIcon /> {t('shopify.markAsDone', 'Mark as Done')}</>)}
        </button>
      </>
    );
  };

  return (
    <div id="page-onboarding">
      {/* Activation Overlay */}
      {activationPhase && <ActivationOverlay phase={activationPhase} t={t} onOpenThemeEditor={handleOpenThemeEditor} />}

      {/* Knowledge Sync Notification */}
      {syncNotification && (
        <div className={`sync-notification ${syncNotification === 'error' ? 'sync-notification--error' : 'sync-notification--info'}`}>
          <span className="sync-notification__text">
            {syncNotification === 'started'
              ? t('shopify.syncStarted')
              : t('shopify.syncFailed')}
          </span>
          <button className="sync-notification__close" onClick={() => setSyncNotification(null)}>&times;</button>
        </div>
      )}

      <div className="onboarding-container">
        {/* Header */}
        <div className="onboarding-header">
          <p className="onboarding-header__welcome">{t('shopify.welcome')}</p>
          <p className="onboarding-header__tip" dangerouslySetInnerHTML={{ __html: `💡 ${t('shopify.tip', { interpolation: { escapeValue: false } })}` }} />

          {/* Progress Card - show skeleton while loading */}
          {loading ? (
            <OnboardingSkeleton />
          ) : (
            <>
              <div className={`progress-card ${showComplete ? 'progress-card--complete' : ''}`}>
                <div className="progress-card__top">
                  <span className="progress-card__label">{showComplete ? t('shopify.setupComplete') : t('shopify.setupProgress')}</span>
                  <span className="progress-card__count">{completed}/{total}</span>
                </div>
                <div className="progress-card__bar">
                  <div className="progress-card__bar-fill" style={{ width: `${(progressCount / total) * 100}%` }} />
                </div>
                <p className="progress-card__status">
                  {showComplete
                    ? t('shopify.liveAndReady')
                    : allRequiredDone
                      ? `✓ ${t('shopify.readyToActivate')}`
                      : t('shopify.requiredRemaining', { count: requiredRemaining, plural: requiredRemaining !== 1 ? 's' : '' })}
                </p>
              </div>

              {/* Steps List */}
              <div className="steps-list">
                {items.map((item) => (
                  <div key={item.id} className={`step-item ${item.status === 'completed' || item.status === 'skipped' ? 'step-item--done' : ''} ${loadingSteps[item.key || item.id] ? 'step-item--loading' : ''} ${item.capabilityLine || item.stats ? 'step-item--has-detail' : ''}`}>
                    {renderStepCircle(item)}

                    <div className="step-content">
                      <div className="step-content__top">
                        <h3 className="step-content__title">{stepText(item, "title")}</h3>
                        {renderBadge(item)}
                      </div>
                      {item.description && <p className="step-content__description">{stepText(item, "description")}</p>}

                      {item.capabilityLine && (
                        <div className="step-capabilities">
                          <span className="step-capabilities__text">{stepText(item, "capabilityLine")}</span>
                          {item.syncedLine && <span className="step-capabilities__synced">{t('shopify.synced', { data: item.syncedLine })}</span>}
                        </div>
                      )}

                      {!item.capabilityLine && item.syncedLine && (
                        <span className="step-capabilities__synced" style={{ display: 'block', marginTop: '4px' }}>{item.syncedLine}</span>
                      )}
                    </div>

                    <div className="step-action">{renderAction(item)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Help Section — hidden until help resources are ready */}
        {false && (
        <div className="help-card">
          <h3 className="help-card__title">{t('helpCard.title')}</h3>
          <p className="help-card__description">{t('helpCard.description')}</p>
          <div className="help-card__actions">
            <button className="help-card__btn help-card__btn--primary">{t('helpCard.watchVideos')}</button>
            <button className="help-card__btn help-card__btn--secondary">{t('helpCard.helpCenter')}</button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
